import {
  Box,
  Button,
  Field,
  HStack,
  Input,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { StreetViewImage } from "../common/StreetViewImage"

const ANALYSIS_STEPS = [
  "Looking up address & property records",
  "Analysing construction, age & insulation",
  "Calculating heating energy demand",
  "Evaluating solar potential & roof orientation",
  "Projecting savings & carbon impact",
  "Preparing your home report",
]

export function AnalysisChecklist() {
  const [completedCount, setCompletedCount] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCompletedCount(prev => {
        if (prev >= ANALYSIS_STEPS.length - 1) {
          clearInterval(intervalRef.current!)
          return prev
        }
        return prev + 1
      })
    }, 2700)

    return () => clearInterval(intervalRef.current!)
  }, [])

  return (
    <VStack gap={3} align="stretch" py={6} px={2}>
      {ANALYSIS_STEPS.map((step, i) => {
        const isDone = i < completedCount
        const isActive = i === completedCount
        const isPending = i > completedCount

        return (
          <HStack
            key={i}
            gap={3}
            opacity={isPending ? 0.35 : 1}
            transition="opacity 0.4s ease"
          >
            <Box flexShrink={0} w="20px" h="20px" display="flex" alignItems="center" justifyContent="center">
              {isDone && (
                <Box
                  w="20px" h="20px" borderRadius="full"
                  bg="green.500" display="flex" alignItems="center" justifyContent="center"
                >
                  <Text color="white" fontSize="11px" fontWeight="bold" lineHeight="1">✓</Text>
                </Box>
              )}
              {isActive && (
                <Spinner size="sm" color="green.500" borderWidth="2px" css={{ "--spinner-track-color": "colors.gray.200" }} />
              )}
              {isPending && (
                <Box w="20px" h="20px" borderRadius="full" border="2px solid" borderColor="gray.200" />
              )}
            </Box>
            <Text
              fontSize="sm"
              fontWeight={isActive ? "semibold" : "normal"}
              color={isDone ? "green.600" : isActive ? "gray.800" : "gray.400"}
              transition="all 0.3s ease"
            >
              {step}
            </Text>
          </HStack>
        )
      })}
    </VStack>
  )
}

export interface AddAddressFormProps {
  onSubmit: (payload: any) => Promise<void> | void
  isSubmitting?: boolean
  apiError?: string | null
  apiErrorTitle?: string | null
  apiErrorLinkText?: string | null
  apiErrorLinkUrl?: string | null
  onClearApiError?: () => void
  brandColor?: string
  hideSubmitButton?: boolean
  showAnalysisChecklist?: boolean
  onAddressSelected?: (details: any) => void
}

declare global {
  interface Window {
    google: any
    initGoogleMaps?: () => void
  }
}

const extractAddressDetails = (place: any) => {
  console.log("place", place)
  if (!place) return null
  let postcode = "",
    streetNumber = "",
    route = "",
    locality = ""
  let administrativeArea = "",
    country = "",
    sublocality = "",
    ward = "",
    postalTown = "",
    adminArea2 = "",
    adminArea3 = ""
  if (place.address_components) {
    place.address_components.forEach((c: any) => {
      if (c.types.includes("postal_code")) postcode = c.long_name
      if (c.types.includes("street_number")) streetNumber = c.long_name
      if (c.types.includes("route")) route = c.long_name
      if (c.types.includes("locality")) locality = c.long_name
      if (c.types.includes("administrative_area_level_1"))
        administrativeArea = c.long_name
      if (c.types.includes("country")) country = c.long_name
      if (c.types.includes("sublocality")) sublocality = c.long_name
      if (c.types.includes("sublocality_level_1")) ward = c.long_name
      if (c.types.includes("postal_town")) postalTown = c.long_name
      if (c.types.includes("administrative_area_level_2"))
        adminArea2 = c.long_name
      if (c.types.includes("administrative_area_level_3"))
        adminArea3 = c.long_name
    })
  }
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ")

  // Enhanced UK city hierarchy with multiple fallback options:
  const city = postalTown || locality || adminArea3 || adminArea2 || ""

  // Alternative: Try to extract city from formatted_address if components fail
  let fallbackCity = ""
  if (!city && place.formatted_address) {
    // Look for patterns like "City, POSTCODE" or "City POSTCODE"
    const addressParts = place.formatted_address
      .split(",")
      .map((part: string) => part.trim())
    for (const part of addressParts) {
      // Skip if it contains numbers (likely postcode) or is the country
      if (
        !/\d/.test(part) &&
        !part.toLowerCase().includes("uk") &&
        part.length > 2
      ) {
        fallbackCity = part
        break
      }
    }
  }

  const finalCity = city || fallbackCity

  const cityParts = [finalCity, sublocality, ward].filter(Boolean)
  const cityString = cityParts.length > 0 ? cityParts.join(", ") : ""
  const addressParts = [streetAddress, cityString, postcode].filter(Boolean)
  const fullAddress = addressParts.join(", ")

  return {
    postcode,
    streetNumber,
    route,
    locality,
    city: finalCity, // Use the more reliable city extraction
    administrativeArea,
    country,
    sublocality,
    ward,
    postalTown,
    adminArea2,
    adminArea3,
    fullAddress,
    shortAddress: streetAddress,
    cityPostcode: cityString ? `${cityString}, ${postcode}` : postcode,
    placeId: place.place_id,
    imageUrl: undefined, // Will be populated later if available
    geometry: place.geometry,
    formattedAddress: place.formatted_address || fullAddress
  }
}

const highlightMatches = (text: string, matches: Array<{ offset: number; length: number }>, color: string) => {
  if (!matches || matches.length === 0) return <>{text}</>
  const parts: React.ReactNode[] = []
  let last = 0
  matches.forEach(({ offset, length }) => {
    if (offset > last) parts.push(text.slice(last, offset))
    parts.push(<span key={offset} style={{ color }}>{text.slice(offset, offset + length)}</span>)
    last = offset + length
  })
  if (last < text.length) parts.push(text.slice(last))
  return <>{parts}</>
}

interface Suggestion {
  prediction: any
  postcode: string | null
}

const AddAddressForm = ({
  onSubmit,
  isSubmitting,
  apiError,
  apiErrorTitle,
  apiErrorLinkText,
  apiErrorLinkUrl,
  onClearApiError,
  brandColor = '#2f855a',
  hideSubmitButton = false,
  showAnalysisChecklist = true,
  onAddressSelected,
}: AddAddressFormProps) => {
  const [inputValue, setInputValue] = useState("")
  const [address, setAddress] = useState("")
  const [addressDetails, setAddressDetails] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)

  const autocompleteServiceRef = useRef<any>(null)
  const placesServiceRef = useRef<any>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const initGoogleServices = () => {
    try {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      )
      setIsGoogleMapsLoaded(true)
    } catch {}
  }

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    if (!apiKey) return
    if (window.google && window.google.maps && window.google.maps.places) {
      initGoogleServices()
      return
    }
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`
    script.async = true
    script.defer = true
    window.initGoogleMaps = () => {
      initGoogleServices()
    }
    document.head.appendChild(script)
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script)
      if (window.initGoogleMaps) delete window.initGoogleMaps
    }
  }, [])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const fetchPostcodeForSuggestion = (placeId: string, index: number) => {
    if (!placesServiceRef.current) return
    placesServiceRef.current.getDetails(
      { placeId, fields: ["address_components"] },
      (place: any, status: string) => {
        if (status !== "OK" || !place) return
        const postcodeComponent = place.address_components?.find((c: any) =>
          c.types.includes("postal_code")
        )
        const postcode = postcodeComponent?.long_name || ""
        setSuggestions(prev =>
          prev.map((s, i) => i === index ? { ...s, postcode } : s)
        )
      }
    )
  }

  const fetchSuggestions = (value: string) => {
    if (!autocompleteServiceRef.current || value.length < 3) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: value,
        types: ["address"],
        componentRestrictions: { country: "gb" },
      },
      (predictions: any[], status: string) => {
        if (status !== "OK" || !predictions) {
          setSuggestions([])
          return
        }
        const initial = predictions.map(p => ({ prediction: p, postcode: null }))
        setSuggestions(initial)
        setShowSuggestions(true)
        setActiveSuggestionIndex(-1)
        // Fetch postcode for each suggestion in parallel
        predictions.forEach((p, i) => fetchPostcodeForSuggestion(p.place_id, i))
      }
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setAddressDetails(null)
    setAddress("")
    setError(null)
    if (onClearApiError) onClearApiError()

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(value), 300)
  }

  const selectSuggestion = (suggestion: Suggestion) => {
    if (!placesServiceRef.current) return
    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.prediction.place_id,
        fields: [
          "formatted_address",
          "name",
          "place_id",
          "types",
          "geometry",
          "address_components",
          "url",
          "utc_offset",
          "vicinity",
          "adr_address",
          "plus_code"
        ],
      },
      (place: any, status: string) => {
        if (status !== "OK" || !place) {
          setError("Could not load address details. Please try again.")
          return
        }
        const details = extractAddressDetails(place)
        if (!details) {
          setError("Could not read address details. Try again.")
          return
        }
        setAddressDetails(details)
        setInputValue(details.formattedAddress)
        setAddress(details.formattedAddress)
        setShowSuggestions(false)
        setSuggestions([])
        setError(null)
        if (onAddressSelected) onAddressSelected(details)
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault()
        if (!inputValue.trim()) setError("Please enter an address")
        else setError("Please select an address from the dropdown for best results")
      }
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveSuggestionIndex(prev => Math.max(prev - 1, -1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (activeSuggestionIndex >= 0) {
        selectSuggestion(suggestions[activeSuggestionIndex])
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  const handleSubmit = async () => {
    if (address.trim() && addressDetails) {
      setError(null)
      await onSubmit({
        address: addressDetails.fullAddress,
        postcode: addressDetails.postcode,
        streetNumber: addressDetails.streetNumber,
        streetName: addressDetails.route,
        locality: addressDetails.locality,
        city: addressDetails.city,
        administrativeArea: addressDetails.administrativeArea,
        country: addressDetails.country,
        sublocality: addressDetails.sublocality,
        ward: addressDetails.ward,
        postalTown: addressDetails.postalTown,
        adminArea2: addressDetails.adminArea2,
        adminArea3: addressDetails.adminArea3,
        shortAddress: addressDetails.shortAddress,
      })
      setInputValue("")
      setAddress("")
      setAddressDetails(null)
    } else if (inputValue.trim()) {
      setError(null)
      await onSubmit({ address: inputValue.trim() })
      setInputValue("")
    } else {
      setError("Please enter an address")
    }
  }

  return (
    <Box borderRadius="lg" borderColor="gray.200">
      <VStack gap={4} align="stretch">
        <Field.Root>
          <Field.Label>What's the home address?</Field.Label>
          <Box ref={containerRef} position="relative" w="100%">
            <Input
              autoFocus
              autoComplete="off"
              placeholder={isGoogleMapsLoaded ? "123 High Street, London, SW1A 1AA" : "Loading Google Maps..."}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              size="lg"
              disabled={!isGoogleMapsLoaded}
            />
            {showSuggestions && suggestions.length > 0 && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={2000}
                bg="white"
                border="1px solid #e2e8f0"
                borderRadius="8px"
                boxShadow="0 8px 20px rgba(17, 17, 17, 0.08)"
                mt={1}
                p="4px"
                overflow="hidden"
              >
                {suggestions.map((s, i) => {
                  const fmt = s.prediction.structured_formatting
                  const main: string = fmt?.main_text || s.prediction.description
                  const mainMatches: Array<{ offset: number; length: number }> = fmt?.main_text_matched_substrings || []
                  const secondary: string = fmt?.secondary_text || ""
                  const secondaryClean = secondary.replace(/(,\s*)?United Kingdom|,?\s*UK$/i, "").trim()
                  const isActive = i === activeSuggestionIndex
                  return (
                    <Box
                      key={s.prediction.place_id}
                      px="18px"
                      py="12px"
                      cursor="pointer"
                      bg={isActive ? "#f7fafc" : "white"}
                      _hover={{ bg: "#f7fafc" }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        selectSuggestion(s)
                      }}
                      onMouseEnter={() => setActiveSuggestionIndex(i)}
                    >
                      <HStack gap="20px" align="center">
                        {/* Google Maps pin icon – matches pac-icon */}
                        <Box flexShrink={0} mt="2px">
                          <svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 0C3.364 0 0 3.364 0 7.5c0 5.625 7.5 12.5 7.5 12.5S15 13.125 15 7.5C15 3.364 11.636 0 7.5 0zm0 10.625A3.125 3.125 0 1 1 7.5 4.375a3.125 3.125 0 0 1 0 6.25z" fill="#9E9E9E"/>
                          </svg>
                        </Box>
                        <Text
                          fontSize="18px"
                          color="#1a202c"
                          lineHeight="1.4"
                          noOfLines={1}
                        >
                          <span style={{ fontWeight: mainMatches.length > 0 ? 600 : 400 }}>
                            {highlightMatches(main, mainMatches, brandColor)}
                          </span>
                          {secondaryClean ? <span style={{ fontWeight: 400 }}>, {secondaryClean}</span> : null}
                          {s.postcode ? <span style={{ fontWeight: 400 }}>, {s.postcode}</span> : null}
                        </Text>
                      </HStack>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Field.Root>
        {addressDetails && (
          <Box
            bg="white"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
            display="flex"
            alignItems="center"
          >
            <StreetViewImage
              imageUrl={addressDetails.imageUrl}
              address={addressDetails.fullAddress}
              svSize="300x200"
              alt={`Street view of ${addressDetails.fullAddress}`}
              h="72px"
              w="96px"
              flexShrink={0}
              objectFit="cover"
              showLabel={false}
            />
            <Box flex={1} px={3} py={2}>
              <Text fontWeight="semibold" fontSize="sm" color="gray.900" lineHeight="1.3">
                {addressDetails.shortAddress}, {addressDetails.postcode}
              </Text>
              {addressDetails.city && (
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  {addressDetails.city}{addressDetails.administrativeArea ? `, ${addressDetails.administrativeArea}` : ''}
                </Text>
              )}
            </Box>
            {!hideSubmitButton && (
              <Box px={3}>
                <Button
                  size="sm"
                  bg={brandColor}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Adding..." : "Add this home"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {apiError && (
          <Box
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            color="red.800"
            px={4}
            py={3}
            borderRadius="md"
            fontSize="sm"
            mt={4}
          >
            <Text fontWeight="bold" mb={1}>
              ⚠️ {apiErrorTitle || "We couldn't check this address"}
            </Text>
            <Text>
              {apiError}
              {apiErrorLinkUrl && apiErrorLinkText && (
                <>
                  {' '}
                  <a
                    href={apiErrorLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2B6CB0', textDecoration: 'underline' }}
                  >
                    {apiErrorLinkText}
                  </a>
                </>
              )}
            </Text>
          </Box>
        )}

        {error && (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        )}
        {/* When submitting show animated analysis checklist */}
        {isSubmitting && showAnalysisChecklist && <AnalysisChecklist />}
      </VStack>
    </Box>
  )
}

export default AddAddressForm
